import { useState, useEffect, useRef, memo } from 'react';
import { Flex, Box } from '@chakra-ui/react';
import { LuPlus } from 'react-icons/lu';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';
import { DEFAULT_MENU_THEME } from '../api/menus';

import { VIEW_STRINGS, getHoverBg } from './menu-content/types';
import type { MenuContentProps, MenuContentCategory, MenuContentBadgeDef, MenuContentItem, MenuContentItemOption } from './menu-content/types';
import HeaderBanner from './menu-content/HeaderBanner';
import MenuHeader from './menu-content/MenuHeader';
import CategoryGrid from './menu-content/CategoryGrid';
import CategoryTabs from './menu-content/CategoryTabs';
import SearchBar from './menu-content/SearchBar';
import CategorySection from './menu-content/CategorySection';
import SortableRow from './menu-content/SortableRow';
import { ImageLightbox, StoreInfoPanel } from './menu-content/Overlays';

// Re-export types for consumers
export type { MenuContentCategory, MenuContentBadgeDef, MenuContentItem, MenuContentItemOption };

const MenuContent = memo(({
  name,
  description,
  currency,
  categories,
  theme,
  storeInfo,
  badgeDefinitions,
  headerImageUrl,
  availableLanguages,
  currentLanguage,
  onLanguageChange,
  onCategoryClick,
  onItemClick,
  onAddCategory,
  onAddItem,
  onReorderCategories,
  onReorderItems,
  onTitleClick,
  onStoreInfoClick,
  onHeaderImageClick,
  page,
  onPageChange,
  onQuickEditItem,
  onQuickEditItemImage,
  onQuickEditCategory,
  onQuickEditCategoryImage,
  onQuickEditTitle,
  onQuickEditDescription,
  onQuickEditHeaderImage,
}: MenuContentProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showStoreInfo, setShowStoreInfo] = useState(false);

  const mt = theme || DEFAULT_MENU_THEME;
  const isEditMode = !!onItemClick;
  const isQuickEditMode = !!onQuickEditItem;
  const hoverBg = getHoverBg(mt.background_color, mt.text_color);
  const vs = VIEW_STRINGS[currentLanguage] || VIEW_STRINGS.en;

  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const isScrollingToCategory = useRef(false);

  // Visible categories/items
  const displayCategories = (isEditMode || isQuickEditMode)
    ? categories
    : categories
        .filter(cat => cat.is_visible !== false)
        .map(cat => ({ ...cat, items: cat.items.filter(item => item.is_visible !== false) }));

  const showCategoryLanding = displayCategories.length > 1 && (page ? page === 1 : !selectedCategoryId);
  const hasHeaderBanner = !!headerImageUrl && showCategoryLanding;

  // Search filter
  const filteredCategories = (!isEditMode && searchQuery.trim())
    ? (() => {
        const q = searchQuery.toLowerCase();
        return displayCategories
          .map(cat => {
            if (cat.name.toLowerCase().includes(q)) return cat;
            return {
              ...cat,
              items: cat.items.filter(item => {
                if (item.name.toLowerCase().includes(q)) return true;
                if (item.description?.toLowerCase().includes(q)) return true;
                if (item.badges?.some(badgeKey => {
                  const def = badgeDefinitions?.find(b => b.key === badgeKey);
                  return (def?.label || badgeKey).toLowerCase().includes(q);
                })) return true;
                return false;
              }),
            };
          })
          .filter(cat => cat.items.length > 0);
      })()
    : displayCategories;

  // --- Navigation ---

  const handleCategoryTabClick = (categoryId: string) => {
    isScrollingToCategory.current = true;
    if (isEditMode && onCategoryClick) {
      onCategoryClick(categoryId);
      isScrollingToCategory.current = false;
    } else if (page && onPageChange) {
      onPageChange(2);
      setActiveTab(categoryId);
      setTimeout(() => {
        document.getElementById(`category-${categoryId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setTimeout(() => { isScrollingToCategory.current = false; }, 800);
      }, 50);
    } else {
      const alreadyOnItems = !!selectedCategoryId;
      setSelectedCategoryId(categoryId);
      setActiveTab(categoryId);
      window.history[alreadyOnItems ? 'replaceState' : 'pushState']({ menuView: 'items', categoryId }, '');
      setTimeout(() => {
        document.getElementById(`category-${categoryId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setTimeout(() => { isScrollingToCategory.current = false; }, 800);
      }, 50);
    }
  };

  const handleBack = () => {
    if (isEditMode || (page && onPageChange)) {
      if (onPageChange) onPageChange(1);
    } else {
      setSelectedCategoryId(null);
      setActiveTab(null);
      setSearchQuery('');
      window.history.back();
    }
  };

  // Browser history integration
  useEffect(() => {
    if (isEditMode || (page && onPageChange)) return;
    const handlePopState = (e: PopStateEvent) => {
      if (e.state?.menuView === 'items') {
        setSelectedCategoryId(e.state.categoryId || null);
        setActiveTab(e.state.categoryId || null);
      } else {
        setSelectedCategoryId(null);
        setSearchQuery('');
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isEditMode, page, onPageChange]);

  // Scroll-spy
  useEffect(() => {
    if (isEditMode || showCategoryLanding) return;
    const categoryIds = displayCategories.map(c => c.id);
    if (categoryIds.length === 0) return;

    const categoryEls = categoryIds
      .map(id => ({ id, el: document.getElementById(`category-${id}`) }))
      .filter((e): e is { id: string; el: HTMLElement } => e.el !== null);
    if (categoryEls.length === 0) return;

    let rafId = 0;
    const handleScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        if (isScrollingToCategory.current) return;
        const threshold = 100;
        let currentId: string = categoryEls[0].id;
        for (const { id, el } of categoryEls) {
          if (el.getBoundingClientRect().top <= threshold) {
            currentId = id;
          } else {
            break;
          }
        }
        setActiveTab(prev => {
          if (prev === currentId) return prev;
          const container = tabsContainerRef.current;
          const tabEl = container?.querySelector(`[data-category-tab="${currentId}"]`) as HTMLElement | null;
          if (container && tabEl) {
            container.scrollTo({ left: tabEl.offsetLeft - container.offsetWidth / 2 + tabEl.offsetWidth / 2, behavior: 'smooth' });
          }
          return currentId;
        });
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
    };
  }, [isEditMode, showCategoryLanding, displayCategories]);

  // --- Render ---

  const soldOutLabel = (isEditMode || isQuickEditMode) ? t('menus.soldOut') : vs.soldOut;

  return (
    <Box style={{ backgroundColor: mt.background_color, color: mt.text_color }} minHeight="100%">
      {/* Header banner */}
      {headerImageUrl && showCategoryLanding && (
        <HeaderBanner
          name={name}
          headerImageUrl={headerImageUrl}
          theme={mt}
          isEditMode={isEditMode}
          availableLanguages={availableLanguages}
          currentLanguage={currentLanguage}
          onLanguageChange={onLanguageChange}
          onHeaderImageClick={onHeaderImageClick}
          onQuickEditHeaderImage={onQuickEditHeaderImage}
          onQuickEditTitle={onQuickEditTitle}
        />
      )}

      {/* Menu header (back button, title, store info, language switcher) */}
      <MenuHeader
        name={name}
        description={description}
        theme={mt}
        storeInfo={storeInfo}
        isEditMode={isEditMode}
        showCategoryLanding={showCategoryLanding}
        hasHeaderBanner={hasHeaderBanner}
        hasManyCategories={displayCategories.length > 1}
        availableLanguages={availableLanguages}
        currentLanguage={currentLanguage}
        vs={vs}
        onLanguageChange={onLanguageChange}
        onTitleClick={onTitleClick}
        onStoreInfoClick={onStoreInfoClick}
        onBack={handleBack}
        onShowStoreInfo={() => setShowStoreInfo(true)}
        onQuickEditTitle={onQuickEditTitle}
        onQuickEditDescription={onQuickEditDescription}
      />

      {/* Category grid (landing page) */}
      {showCategoryLanding && (
        <CategoryGrid
          categories={displayCategories}
          theme={mt}
          hoverBg={hoverBg}
          isEditMode={isEditMode}
          onCategoryClick={onCategoryClick}
          onCategorySelect={handleCategoryTabClick}
          onAddCategory={onAddCategory}
          onReorderCategories={onReorderCategories}
          onQuickEditCategory={onQuickEditCategory}
          onQuickEditCategoryImage={onQuickEditCategoryImage}
        />
      )}

      {/* Category tabs */}
      {!showCategoryLanding && (displayCategories.length > 0 || isEditMode) && (
        <CategoryTabs
          ref={tabsContainerRef}
          categories={displayCategories}
          activeTab={activeTab}
          theme={mt}
          hoverBg={hoverBg}
          isEditMode={isEditMode}
          onTabClick={handleCategoryTabClick}
          onAddCategory={onAddCategory}
          onReorderCategories={onReorderCategories}
          onQuickEditCategory={onQuickEditCategory}
        />
      )}

      {/* Search */}
      {!isEditMode && !isQuickEditMode && !showCategoryLanding && displayCategories.length > 0 && (
        <SearchBar query={searchQuery} onChange={setSearchQuery} placeholder={vs.search} theme={mt} hoverBg={hoverBg} />
      )}

      {/* Items list */}
      {!showCategoryLanding && (
        <Box px="20px" py="16px">
          {displayCategories.length === 0 ? (
            <Flex direction="column" alignItems="center" justifyContent="center" py="48px">
              <Box fontSize="14px" color={mt.secondary_text_color}>{t('menus.noCategoriesYet')}</Box>
            </Flex>
          ) : isEditMode && onReorderCategories ? (
            <DndContext collisionDetection={closestCenter} onDragEnd={onReorderCategories}>
              <SortableContext items={displayCategories.map(c => c.id)} strategy={verticalListSortingStrategy}>
                {displayCategories.map((category) => (
                  <SortableRow key={category.id} id={category.id}>
                    {({ dragRef, dragStyle }) => (
                      <Box ref={dragRef} style={dragStyle} id={`category-${category.id}`} mb="24px" css={{ scrollMarginTop: '48px' }}>
                        <CategorySection
                          category={category}
                          theme={mt}
                          hoverBg={hoverBg}
                          currency={currency}
                          isEditMode={isEditMode}
                          badgeDefinitions={badgeDefinitions}
                          soldOutLabel={soldOutLabel}
                          onCategoryClick={onCategoryClick}
                          onItemClick={onItemClick}
                          onAddItem={onAddItem}
                          onReorderItems={onReorderItems}
                          onImageClick={setLightboxUrl}
                        />
                      </Box>
                    )}
                  </SortableRow>
                ))}
              </SortableContext>
            </DndContext>
          ) : filteredCategories.length === 0 && searchQuery.trim() ? (
            <Flex direction="column" alignItems="center" justifyContent="center" py="48px">
              <Box fontSize="14px" color={mt.secondary_text_color}>{vs.noResults}</Box>
            </Flex>
          ) : (
            filteredCategories.map((category) => (
              <Box key={category.id} id={`category-${category.id}`} css={{ scrollMarginTop: '48px' }}>
                <CategorySection
                  category={category}
                  theme={mt}
                  hoverBg={hoverBg}
                  currency={currency}
                  isEditMode={isEditMode}
                  badgeDefinitions={badgeDefinitions}
                  soldOutLabel={soldOutLabel}
                  onCategoryClick={onCategoryClick}
                  onItemClick={onItemClick}
                  onAddItem={onAddItem}
                  onReorderItems={onReorderItems}
                  onImageClick={setLightboxUrl}
                  onQuickEditItem={onQuickEditItem}
                  onQuickEditItemImage={onQuickEditItemImage}
                  onQuickEditCategory={onQuickEditCategory}
                />
              </Box>
            ))
          )}

          {isEditMode && onAddCategory && (
            <Flex
              alignItems="center"
              justifyContent="center"
              gap="6px"
              cursor="pointer"
              p="12px"
              borderRadius="8px"
              borderWidth="1px"
              borderStyle="dashed"
              borderColor={mt.border_color}
              _hover={{ backgroundColor: hoverBg, borderColor: mt.secondary_text_color }}
              onClick={onAddCategory}
            >
              <LuPlus style={{ width: 14, height: 14, color: mt.secondary_text_color }} />
              <Box fontSize="13px" color={mt.secondary_text_color} fontWeight="500">{t('menus.addCategory')}</Box>
            </Flex>
          )}
        </Box>
      )}

      {/* Overlays */}
      {lightboxUrl && <ImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />}
      {showStoreInfo && storeInfo && <StoreInfoPanel storeInfo={storeInfo} theme={mt} strings={vs} onClose={() => setShowStoreInfo(false)} />}
    </Box>
  );
});

export default MenuContent;
